import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IonCard, IonCardHeader, IonCardTitle, IonList, IonItemGroup, IonItemDivider,
  IonItem, IonLabel, IonAvatar, IonIcon, IonSpinner,
} from "@ionic/react";
import { createOutline, imageOutline } from "ionicons/icons";
import EditSheet from "@/components/EditSheet";
import ColorPicker from "@/components/ColorPicker";
import BrandingPreview from "@/components/BrandingPreview";
import { useSavedAssets, AssetManagerModal, invalidateSavedAssets, currentUserId } from "@/components/SavedAssets";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { toast } from "@/utils/toast";
import {
  DEFAULT_BRAND_COLOR, normalizeHex, applyBrandColor, storeBrandColor, contrastRatio,
} from "@/utils/brandColor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The Branding area of /app Preferences: the signed-in user's reusable
// signatures and logos plus their own brand colour, in the admin Branding
// page's design flow — one card, a Design Elements divider, avatar-led rows
// that open a manager, and the shared previews of the colour in use.
// Pro and Enterprise plans only; anyone else is sent to Billing.
export default function AppBrandingCard() {
  const navigate = useNavigate();
  const signedIn = isUserLoggedIn();
  const { hasAccess, assets, primaryColor, loading, refresh } = useSavedAssets();
  const [manage, setManage] = useState(null); // "signature" | "logo"
  const [sheet, setSheet] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_BRAND_COLOR);
  const [saving, setSaving] = useState(false);
  // What was just saved, shown immediately: the assets refetch that carries
  // the colour back can lag, and the previews must not snap to the old one.
  const [justSaved, setJustSaved] = useState(null);

  useAppLive(() => { if (signedIn) refresh(); }); // an upgrade shows up without a reload

  // Anyone without Branding goes straight to Billing rather than reading a
  // page about which plans carry it. Signed-out visitors included: the plans
  // are exactly what they need to see.
  useEffect(() => {
    if (loading || hasAccess) return;
    navigate("/app/billing", { replace: true });
  }, [loading, hasAccess, navigate]);

  const sigs = assets.filter((a) => a.kind === "signature");
  const logos = assets.filter((a) => a.kind === "logo");
  const countText = (n) => (n === 0 ? "None saved yet" : n === 1 ? "1 saved" : `${n} saved`);

  const saved = normalizeHex(justSaved || primaryColor) || DEFAULT_BRAND_COLOR;
  // while the sheet is open the previews follow the colour being picked
  const color = sheet ? (normalizeHex(draft) || saved) : saved;

  const openColor = () => { setDraft(saved); setSheet(true); };
  // Picking recolours the top bar and buttons on the spot, for the look of
  // it; closing without the checkmark puts the saved colour straight back.
  const previewDraft = (v) => {
    setDraft(v);
    const hex = normalizeHex(v);
    if (hex) applyBrandColor(hex);
  };
  const closeColor = () => { setSheet(false); applyBrandColor(saved); };
  const saveColor = async () => {
    const hex = normalizeHex(draft);
    if (!hex) { toast.error("Enter a colour as a 6-digit hex code, like #2fd2a8"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/branding`, {
        method: "PUT", headers: userHeaders({ "Content-Type": "application/json" }),
        // the default saves as an EXPLICIT colour: an empty value means
        // "inherit", and with a business active that would fall back to the
        // account's old colour instead of the green being chosen
        body: JSON.stringify({ primaryColor: hex }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
      const next = normalizeHex(d.primaryColor) || DEFAULT_BRAND_COLOR;
      setJustSaved(next); // the previews follow at once; the refetch catches up behind
      storeBrandColor(next, "app", currentUserId());
      applyBrandColor(next);
      invalidateSavedAssets(); // the colour rides back on the assets response
      toast.success("Primary color updated");
      setSheet(false);
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  // The bar and the buttons carry white text on this colour, whatever it is,
  // so say when that will be hard to read rather than quietly darkening it.
  const onChrome = contrastRatio("#ffffff", color);
  const warn = onChrome < 4.5
    ? `White text on this colour sits at ${onChrome.toFixed(1)}:1. Below 4.5:1 it is hard to read at small sizes. A deeper shade reads better.`
    : null;

  const logoArt = logos[0] ? <img alt="" src={logos[0].data} /> : <IonIcon icon={imageOutline} />;

  return (
    <>
      <div className="admin-profile admin-branding app-branding" style={{ maxWidth: 1200, margin: "0 auto" }}>
        {loading || !hasAccess ? (
          // still loading, or on its way to Billing: nothing to read in passing
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
        ) : (
          <IonCard className="branding-card">
            <IonCardHeader><IonCardTitle>Branding</IonCardTitle></IonCardHeader>
            <IonList>
              <IonItemGroup>
                <IonItemDivider className="border-top"><IonLabel>Design Elements</IonLabel></IonItemDivider>
                <IonItem button detail className="branding-row" onClick={() => setManage("signature")}>
                  <IonAvatar slot="start" className="logo-element">
                    <IonIcon icon={createOutline} />
                  </IonAvatar>
                  <IonLabel>Signatures<p>{countText(sigs.length)}</p></IonLabel>
                </IonItem>
                <IonItem button detail className="branding-row" onClick={() => setManage("logo")}>
                  <IonAvatar slot="start" className="logo-element">
                    {logos[0] ? <img src={logos[0].data} alt="" /> : <IonIcon icon={imageOutline} />}
                  </IonAvatar>
                  <IonLabel>Logos<p>{countText(logos.length)}</p></IonLabel>
                </IonItem>
                <IonItem button detail className="branding-row" onClick={openColor}>
                  <IonAvatar slot="start" className="color-element" style={{ backgroundColor: saved }} />
                  <IonLabel>Primary Color<p>{saved === DEFAULT_BRAND_COLOR ? "Theme green" : saved}</p></IonLabel>
                </IonItem>
              </IonItemGroup>
              <IonItemGroup>
                <BrandingPreview color={color} logoArt={logoArt} />
              </IonItemGroup>
            </IonList>
          </IonCard>
        )}
      </div>
      {manage && <AssetManagerModal kind={manage} onClose={() => setManage(null)} />}

      {sheet && (
        <EditSheet title="Primary Color" onClose={closeColor} onSave={saveColor} saving={saving} className="color-sheet">
          <IonCard className="color-picker-card">
            <div className="color-picker-head">Color *</div>
            <ColorPicker value={draft} onChange={previewDraft} />
          </IonCard>
          {warn && <p className="admin-profile-sheet-note" style={{ color: "var(--ion-color-danger)" }}>{warn}</p>}
          <p className="admin-profile-sheet-note">Used for your top bar and the buttons in that colour. The default is {DEFAULT_BRAND_COLOR}.</p>
        </EditSheet>
      )}
    </>
  );
}
